import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import type { ILead } from 'src/services/api/leads/types';
import { useNotifications } from 'src/notifications';
import { apiClient } from 'src/services/api/client';
import { GET_LEADS_PATH } from 'src/services/api/leads/constants';
import { queryKeys } from 'src/services/api/constants';
import { getApiErrorMessage } from 'src/services/api/utils';

type IAssociateLeadsResponse = {
    leads: ILead[];
}

type IAssociatedLeadsRequest = {
    lead_uuids: string[];
}

export const useAssociateLeads = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        ILead[],
        IStandardErrorResponse,
        IAssociatedLeadsRequest
    >(async (payload) => {
        const path = `${GET_LEADS_PATH}/coapplicants`;
        const res = await apiClient(path, {
            method: 'post',
            json: payload,
        }).json<IAssociateLeadsResponse>();
        return res.leads;
    }, {
        onSuccess: (data) => {
            if (!data || data.length === 0) {
                addNotification('Leads associated', 'success');
                return;
            }

            const leadsWithCoapplicants = data.map(lead => ({
                ...lead,
                coapplicants: data.filter(l => l.uuid !== lead.uuid)
            }));

            const updatedLeadsMap = new Map(leadsWithCoapplicants.map(lead => [lead.uuid, lead]));
            const groupId = data[0]?.coapplicant_leads_group_id;

            queryClient.setQueryData<ILead[]>(queryKeys.leads, (oldData) => {
                if (!oldData) {
                    return leadsWithCoapplicants;
                }

                const updated = oldData.map(lead => {
                    const updatedLead = updatedLeadsMap.get(lead.uuid);
                    if (updatedLead) {
                        queryClient.setQueryData<ILead>([queryKeys.leads, lead.uuid], updatedLead);
                        return updatedLead;
                    }

                    if (groupId && lead.coapplicant_leads_group_id === groupId) {
                        const _updatedLead = {
                            ...lead,
                            coapplicants: leadsWithCoapplicants.filter(l => l.uuid !== lead.uuid)
                        };

                        queryClient.setQueryData<ILead>([queryKeys.leads, lead.uuid], _updatedLead);
                        return _updatedLead;
                    }

                    return lead;
                });

                return updated;
            });

            addNotification('Leads associated', 'success');
        },
        onError: (error) => {
            const errorMessage = getApiErrorMessage(error) || 'Failed to associate leads';
            addNotification(errorMessage, 'error');
        }
    });
};